import React, { useState, useEffect, useRef } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine, ReferenceArea } from 'recharts';
import { maturationService } from '../../../services/maturationService';
import { calculateGrowthVelocity, detectPHVWithConfidence, assessDataQualityWithHardRequirements, calculateAgeInYears } from '../../../utils/growthCalculations';

const GrowthDashboard = ({ child, onAddMeasurement, onViewInsights, onBack }) => {
  const [loading, setLoading] = useState(true);
  const [measurements, setMeasurements] = useState([]);
  const [growthData, setGrowthData] = useState(null);
  const [phvResult, setPhvResult] = useState(null);
  const [dataQuality, setDataQuality] = useState(null);
  const [showTransparency, setShowTransparency] = useState(false);
  const heightChartRef = useRef(null);
  const velocityChartRef = useRef(null);

  useEffect(() => {
    loadMeasurements();
  }, [child?.id]);

  const loadMeasurements = async () => {
    try {
      setLoading(true);
      const data = await maturationService?.getMeasurements(child?.id);
      setMeasurements(data);

      if (data?.length > 0) {
        const { heightVsAge, velocityVsAge, dataQualityFlags, suspiciousIntervals } = calculateGrowthVelocity(data, child?.dateOfBirth);
        setGrowthData({ heightVsAge, velocityVsAge, dataQualityFlags, suspiciousIntervals });

        const currentAge = calculateAgeInYears(child?.dateOfBirth, new Date());
        
        const quality = assessDataQualityWithHardRequirements(data, child?.dateOfBirth, child?.sexAtBirth, suspiciousIntervals);
        setDataQuality(quality);

        const phv = detectPHVWithConfidence(velocityVsAge, child?.sexAtBirth, currentAge, quality);
        setPhvResult(phv);
      }
    } catch (err) {
      console.error('Error loading measurements:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateTransparencyMetrics = () => {
    if (!measurements || measurements?.length === 0) return null;
    
    const sorted = [...measurements]?.sort((a, b) => 
      new Date(a?.measurementDatetime) - new Date(b?.measurementDatetime)
    );
    
    const firstDate = new Date(sorted?.[0]?.measurementDatetime);
    const lastDate = new Date(sorted?.[sorted?.length - 1]?.measurementDatetime);
    const spanDays = (lastDate - firstDate) / (1000 * 60 * 60 * 24);
    
    // Calculate median days between measures
    const gaps = [];
    for (let i = 0; i < sorted?.length - 1; i++) {
      const gap = (new Date(sorted?.[i + 1]?.measurementDatetime) - new Date(sorted?.[i]?.measurementDatetime)) / (1000 * 60 * 60 * 24);
      gaps?.push(gap);
    }
    const medianGap = gaps?.length > 0 ? gaps?.sort((a, b) => a - b)?.[Math.floor(gaps?.length / 2)] : 0;
    
    return {
      totalMeasurements: measurements?.length,
      spanDays: Math.round(spanDays),
      spanMonths: (spanDays / 30.44)?.toFixed(1),
      medianGap: Math.round(medianGap),
      flaggedIntervals: growthData?.suspiciousIntervals?.length || 0,
      confidenceGrade: dataQuality?.grade || 'N/A',
      gradeReason: dataQuality?.message || 'Not enough data'
    };
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload?.length) {
      return (
        <div className="bg-surface border border-border rounded-lg p-3 shadow-lg">
          <p className="text-sm font-medium text-text-primary mb-1">
            Age: {label?.toFixed(1)} years
          </p>
          {payload?.map((entry, index) => (
            <div key={index} className="flex items-center space-x-2 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: entry?.color }} />
              <span className="text-text-secondary">{entry?.name}:</span>
              <span className="font-medium text-text-primary">{entry?.value?.toFixed(1)} {entry?.unit}</span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  const getQualityColor = (grade) => {
    const colors = {
      'A': 'text-success',
      'B': 'text-info',
      'C': 'text-warning',
      'D': 'text-error',
      'F': 'text-error'
    };
    return colors?.[grade] || 'text-text-secondary';
  };

  const getQualityBgColor = (grade) => {
    const colors = {
      'A': 'bg-success/10 border-success',
      'B': 'bg-info/10 border-info',
      'C': 'bg-warning/10 border-warning',
      'D': 'bg-error/10 border-error'
    };
    return colors?.[grade] || 'bg-surface border-border';
  };

  const getPHVStatusColor = (status) => {
    const colors = {
      'detected': 'text-success',
      'not_assessed': 'text-text-secondary',
      'insufficient_data': 'text-warning',
      'not_detected': 'text-error'
    };
    return colors?.[status] || 'text-text-secondary';
  };

  const transparencyMetrics = calculateTransparencyMetrics();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-text-secondary">Loading growth data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <Button 
            onClick={onBack}
            variant="ghost"
            size="sm"
            iconName="ArrowLeft"
          >
            Back
          </Button>
          <h1 className="text-3xl font-bold text-text-primary ml-4">Growth Dashboard</h1>
        </div>
        <div className="flex gap-3">
          <Button 
            onClick={onViewInsights}
            variant="outline"
            iconName="Lightbulb"
            disabled={!growthData || measurements?.length < 3}
          >
            View Insights
          </Button>
          <Button 
            onClick={onAddMeasurement}
            iconName="Plus"
          >
            Add Measurement
          </Button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-text-primary">Child Profile</h2>
        </div>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <p className="text-text-secondary text-sm">Nickname</p>
            <p className="text-text-primary font-medium">{child?.nickname}</p>
          </div>
          <div>
            <p className="text-text-secondary text-sm">Current Age</p>
            <p className="text-text-primary font-medium">{calculateAgeInYears(child?.dateOfBirth, new Date())?.toFixed(1)} years</p>
          </div>
          <div>
            <p className="text-text-secondary text-sm">Total Measurements</p>
            <p className="text-text-primary font-medium">{measurements?.length}</p>
          </div>
        </div>
      </div>

      {dataQuality && (
        <div className="grid md:grid-cols-2 gap-4">
          <div className={`border rounded-lg p-6 ${getQualityBgColor(dataQuality?.grade)}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-text-primary">Data Quality Grade</h3>
              <span className={`text-3xl font-bold ${getQualityColor(dataQuality?.grade)}`}>
                {dataQuality?.grade}
              </span>
            </div>
            <p className="text-text-secondary text-sm mb-2">
              Confidence: <span className="font-medium capitalize">{dataQuality?.confidence}</span>
            </p>
            <p className="text-text-secondary text-sm mb-3">{dataQuality?.message}</p>
            
            {dataQuality?.grade === 'D' && (
              <div className="mt-4 pt-4 border-t border-error/20">
                <p className="text-sm font-medium text-text-primary mb-2">Hard Requirements Status:</p>
                <div className="space-y-1 text-xs">
                  <div className="flex items-center">
                    <Icon name={dataQuality?.hardRequirements?.minMeasurements ? "CheckCircle" : "XCircle"} 
                          className={`w-4 h-4 mr-2 ${dataQuality?.hardRequirements?.minMeasurements ? 'text-success' : 'text-error'}`} />
                    <span className="text-text-secondary">≥8 measurements spanning ≥10 months</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name={dataQuality?.hardRequirements?.windowMeasurements ? "CheckCircle" : "XCircle"} 
                          className={`w-4 h-4 mr-2 ${dataQuality?.hardRequirements?.windowMeasurements ? 'text-success' : 'text-error'}`} />
                    <span className="text-text-secondary">≥4 measurements in candidate window</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name={dataQuality?.hardRequirements?.medianGap ? "CheckCircle" : "XCircle"} 
                          className={`w-4 h-4 mr-2 ${dataQuality?.hardRequirements?.medianGap ? 'text-success' : 'text-error'}`} />
                    <span className="text-text-secondary">Median gap ≤45 days</span>
                  </div>
                  <div className="flex items-center">
                    <Icon name={dataQuality?.hardRequirements?.suspiciousIntervals ? "CheckCircle" : "XCircle"} 
                          className={`w-4 h-4 mr-2 ${dataQuality?.hardRequirements?.suspiciousIntervals ? 'text-success' : 'text-error'}`} />
                    <span className="text-text-secondary">≤20% suspicious intervals</span>
                  </div>
                </div>
              </div>
            )}
            
            {dataQuality?.improvements?.length > 0 && (
              <div className="mt-4 pt-4 border-t border-border">
                <p className="text-sm font-medium text-text-primary mb-2">How to improve:</p>
                <ul className="space-y-1">
                  {dataQuality?.improvements?.map((improvement, idx) => (
                    <li key={idx} className="text-xs text-text-secondary flex items-start">
                      <span className="mr-2">•</span>
                      <span>{improvement}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-text-primary">PHV Status</h3>
              <Icon name="TrendingUp" className={`w-6 h-6 ${getPHVStatusColor(phvResult?.status)}`} />
            </div>
            <p className={`text-sm font-medium mb-2 ${getPHVStatusColor(phvResult?.status)}`}>
              {phvResult?.status === 'insufficient_data' && dataQuality?.grade === 'D' ?'INSUFFICIENT DATA' : phvResult?.status?.replace('_', ' ')?.toUpperCase()}
            </p>
            <p className="text-text-secondary text-sm">{phvResult?.message}</p>
            {phvResult?.status === 'detected' && (
              <>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-text-secondary text-xs">Peak Velocity: {phvResult?.peakVelocity?.toFixed(1)} cm/year</p>
                  <p className="text-text-secondary text-xs">Confidence: {phvResult?.confidence}</p>
                  <p className="text-text-secondary text-xs">Grade: {phvResult?.confidenceGrade}</p>
                  {phvResult?.confidenceGrade === 'C' && (
                    <div className="mt-2 p-2 bg-warning/10 rounded text-xs text-warning">
                      ⚠ Low confidence - interpret with caution
                    </div>
                  )}
                </div>
                <div className="mt-3 pt-3 border-t border-border">
                  <p className="text-xs italic text-text-secondary">Estimated range, not a guarantee.</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {transparencyMetrics && (
        <div className="bg-card border border-border rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-text-primary flex items-center">
              <Icon name="Info" className="w-5 h-5 mr-2 text-primary" />
              Data Transparency Panel
            </h3>
            <Button 
              onClick={() => setShowTransparency(!showTransparency)}
              variant="ghost"
              size="sm"
              iconName={showTransparency ? 'ChevronUp' : 'ChevronDown'}
            >
              {showTransparency ? 'Hide' : 'Show'} Details
            </Button>
          </div>
          
          {showTransparency && (
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-surface rounded-lg p-4">
                <p className="text-text-secondary text-sm mb-1">Measurements Entered</p>
                <p className="text-text-primary font-bold text-2xl">{transparencyMetrics?.totalMeasurements}</p>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <p className="text-text-secondary text-sm mb-1">Time Span Covered</p>
                <p className="text-text-primary font-bold text-2xl">{transparencyMetrics?.spanMonths} months</p>
                <p className="text-text-secondary text-xs">({transparencyMetrics?.spanDays} days)</p>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <p className="text-text-secondary text-sm mb-1">Median Days Between Measures</p>
                <p className="text-text-primary font-bold text-2xl">{transparencyMetrics?.medianGap}</p>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <p className="text-text-secondary text-sm mb-1">Flagged Intervals</p>
                <p className="text-text-primary font-bold text-2xl">{transparencyMetrics?.flaggedIntervals}</p>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <p className="text-text-secondary text-sm mb-1">Confidence Grade</p>
                <p className={`font-bold text-2xl ${getQualityColor(transparencyMetrics?.confidenceGrade)}`}>
                  {transparencyMetrics?.confidenceGrade}
                </p>
              </div>
              <div className="bg-surface rounded-lg p-4">
                <p className="text-text-secondary text-sm mb-1">Grade Reason</p>
                <p className="text-text-primary text-sm">{transparencyMetrics?.gradeReason}</p>
              </div>
            </div>
          )}
          
          <div className="mt-4 p-3 bg-info/10 border border-info rounded text-sm text-text-secondary">
            <Icon name="Info" className="w-4 h-4 inline mr-2 text-info" />
            This panel shows how the data quality grade is determined. Estimates are based on patterns in your measurements and may be wrong when data is limited or inconsistent.
          </div>
        </div>
      )}

      {growthData?.suspiciousIntervals?.length > 0 && (
        <div className="bg-warning/10 border border-warning rounded-lg p-4">
          <div className="flex items-start">
            <Icon name="AlertTriangle" className="w-5 h-5 text-warning mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-medium text-text-primary mb-2">Suspicious Intervals Detected</h3>
              <p className="text-sm text-text-secondary mb-2">
                {growthData?.suspiciousIntervals?.length} interval(s) flagged based on data quality rules:
              </p>
              <ul className="space-y-1">
                {growthData?.suspiciousIntervals?.slice(0, 3)?.map((interval, idx) => (
                  <li key={idx} className="text-sm text-text-secondary">
                    • Age {interval?.fromAge?.toFixed(1)}-{interval?.toAge?.toFixed(1)} years: {interval?.reasons?.join(', ')}
                  </li>
                ))}
                {growthData?.suspiciousIntervals?.length > 3 && (
                  <li className="text-sm text-text-secondary">
                    • +{growthData?.suspiciousIntervals?.length - 3} more flagged intervals
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {measurements?.length === 0 ? (
        <div className="bg-card border border-border rounded-lg p-12 text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-primary/10 p-6 rounded-full">
              <Icon name="Ruler" className="w-12 h-12 text-primary" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-text-primary mb-2">No Measurements Yet</h2>
          <p className="text-text-secondary mb-6">Add your first measurement to start tracking growth</p>
          <Button onClick={onAddMeasurement} iconName="Plus">
            Add First Measurement
          </Button>
        </div>
      ) : (
        <>
          <div className="bg-card border border-border rounded-lg p-6">
            <h3 className="text-lg font-semibold text-text-primary mb-4">Height Over Time</h3>
            <p className="text-text-secondary text-sm mb-4">
              Tracking height measurements over time. The smoothed curve shows the general growth trend.
            </p>
            <div className="h-80" ref={heightChartRef}>
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={growthData?.heightVsAge} margin={{ top: 5, right: 30, left: 20, bottom: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis 
                    dataKey="ageInYears" 
                    stroke="#64748B"
                    fontSize={12}
                    label={{ value: 'Age (years)', position: 'insideBottom', offset: -5 }}
                    tickFormatter={(value) => value?.toFixed(1)}
                  />
                  <YAxis 
                    stroke="#64748B"
                    fontSize={12}
                    label={{ value: 'Height (cm)', angle: -90, position: 'insideLeft' }}
                    tickFormatter={(value) => value?.toFixed(0)}
                    domain={['dataMin - 5', 'dataMax + 5']}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend verticalAlign="top" height={36} />
                  <Line 
                    type="monotone" 
                    dataKey="heightCm" 
                    stroke="#2563EB" 
                    strokeWidth={3}
                    dot={{ fill: '#2563EB', strokeWidth: 2, r: 4 }}
                    activeDot={{ r: 6 }}
                    name="Height"
                    unit="cm"
                  />
                  {phvResult?.status === 'detected' && (
                    <>
                      <ReferenceLine 
                        x={phvResult?.phvAge} 
                        stroke="#059669" 
                        strokeDasharray="5 5"
                        label={{ value: 'PHV', position: 'top', fill: '#059669', fontSize: 12 }}
                      />
                      <ReferenceArea
                        x1={phvResult?.phvAgeRange?.earliest}
                        x2={phvResult?.phvAgeRange?.latest}
                        fill="#059669"
                        fillOpacity={0.1}
                        label={{ value: 'PHV Range', position: 'insideTop', fill: '#059669', fontSize: 10 }}
                      />
                    </>
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {growthData?.velocityVsAge?.length > 0 && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-text-primary mb-2">Height Velocity Over Time</h3>
              <p className="text-text-secondary text-sm mb-4">
                Annualized growth velocity calculated from interval changes. Peak height velocity (PHV) represents the maximum growth rate during adolescence.
              </p>
              <div className="h-80" ref={velocityChartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={growthData?.velocityVsAge} margin={{ top: 5, right: 30, left: 30, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis 
                      dataKey="ageInYears" 
                      stroke="#64748B"
                      fontSize={12}
                      label={{ value: 'Age (years)', position: 'insideBottom', offset: -5 }}
                      tickFormatter={(value) => value?.toFixed(1)}
                    />
                    <YAxis 
                      stroke="#64748B"
                      fontSize={12}
                      label={{ value: 'Velocity (cm/year)', angle: -90, position: 'center' }}
                      tickFormatter={(value) => value?.toFixed(1)}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend verticalAlign="top" height={36} />
                    <Line 
                      type="monotone" 
                      dataKey="velocityCmPerYear" 
                      stroke="#059669" 
                      strokeWidth={3}
                      dot={{ fill: '#059669', strokeWidth: 2, r: 4 }}
                      activeDot={{ r: 6 }}
                      name="Velocity"
                      unit="cm/year"
                    />
                    {phvResult?.status === 'detected' && (
                      <>
                        <ReferenceLine 
                          x={phvResult?.phvAge} 
                          stroke="#DC2626" 
                          strokeDasharray="5 5"
                          label={{ value: 'PHV', position: 'top', fill: '#DC2626', fontSize: 12 }}
                        />
                        <ReferenceArea
                          x1={phvResult?.phvAgeRange?.earliest}
                          x2={phvResult?.phvAgeRange?.latest}
                          fill="#DC2626"
                          fillOpacity={0.1}
                        />
                      </>
                    )}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              {phvResult?.status === 'detected' && (
                <div className="mt-3 p-3 bg-info/10 border border-info rounded text-sm text-text-secondary">
                  <Icon name="Info" className="w-4 h-4 inline mr-2 text-info" />
                  PHV detected at {phvResult?.phvAge?.toFixed(1)} years (range: {phvResult?.phvAgeRange?.earliest?.toFixed(1)}-{phvResult?.phvAgeRange?.latest?.toFixed(1)} years). 
                  <span className="font-medium"> Estimated range, not a guarantee.</span>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GrowthDashboard;